// Browser-side media scenarios for the public /test page.
// Each scenario creates two peers in the SAME tab, has them publish to and
// subscribe from each other through the Cloudflare SFU, and exposes the
// resulting MediaStreams for the UI to render. The harness asserts that
// real media frames flow through getStats().

import { ConsoleEvent, ConsolePush } from './scenarios'
import { TestPeer, waitFor } from './peer'

export interface MediaPeer {
  label: 'alice' | 'bob'
  participantId: string
  peer: TestPeer
}

export interface MediaSession {
  alice: MediaPeer
  bob: MediaPeer
  roomId: string
  cleanup: () => Promise<void>
}

export interface TimingStep {
  label: string
  ms: number
}

export interface MediaResult {
  ok: boolean
  durationMs: number
  error?: string
  // Per-step timing breakdown — sums to durationMs (within a few ms of overhead).
  timeline: TimingStep[]
  // The headline "time to first frame" — useful as a single SLA-style number.
  connectMs: number | null
  stats: {
    aliceRx: { kind: string; packets: number; frames?: number; fps?: number; resolution?: string; kbps: number }[]
    bobRx: { kind: string; packets: number; frames?: number; fps?: number; resolution?: string; kbps: number }[]
  }
}

export type MediaKind = 'audio' | 'video' | 'screen'

// Creates the two peers without joining — scenarios run joinRoom and
// getUserMedia in parallel (capture doesn't depend on signaling, so
// serializing them only adds latency).
function createSession(opts: { basePath: string; push: ConsolePush }, kind: MediaKind): MediaSession {
  const suffix = Math.random().toString(36).slice(2, 8)
  const roomId = `media-${kind}-${suffix}`

  const onLog = (peerLabel: 'alice' | 'bob') =>
    (level: ConsoleEvent['level'] | 'info', text: string, detail?: string) => {
      const lvl: ConsoleEvent['level'] = level === 'info' ? 'info' : level
      opts.push({ level: lvl, text: `[${peerLabel}] ${text}`, detail })
    }

  const alice = new TestPeer({ basePath: opts.basePath, participantId: `media-alice-${suffix}`, displayName: 'Alice', onLog: onLog('alice') })
  const bob = new TestPeer({ basePath: opts.basePath, participantId: `media-bob-${suffix}`, displayName: 'Bob', onLog: onLog('bob') })

  return {
    alice: { label: 'alice', participantId: `media-alice-${suffix}`, peer: alice },
    bob: { label: 'bob', participantId: `media-bob-${suffix}`, peer: bob },
    roomId,
    cleanup: async () => {
      await Promise.allSettled([alice.leave(), bob.leave()])
    },
  }
}

function summarize(stats: import('./peer').InboundTrackStats[], elapsedSeconds: number): MediaResult['stats']['aliceRx'] {
  return stats.map((s) => ({
    kind: s.kind,
    packets: s.packetsReceived,
    frames: s.framesDecoded,
    fps: s.framesPerSecond,
    resolution: s.frameWidth && s.frameHeight ? `${s.frameWidth}x${s.frameHeight}` : undefined,
    kbps: elapsedSeconds > 0 ? Math.round((s.bytesReceived * 8) / 1000 / elapsedSeconds) : 0,
  }))
}

async function timeStep<T>(label: string, timeline: TimingStep[], fn: () => Promise<T>): Promise<T> {
  const t0 = performance.now()
  try {
    return await fn()
  } finally {
    timeline.push({ label, ms: Math.round(performance.now() - t0) })
  }
}

export async function runAudioScenario(opts: { basePath: string; push: ConsolePush }): Promise<{ result: MediaResult; session: MediaSession }> {
  const started = performance.now()
  const timeline: TimingStep[] = []
  const session = createSession(opts, 'audio')
  try {
    // Lobby: device capture + TURN credentials + PeerConnection happen
    // BEFORE the call starts — the industry preview pattern (Agora
    // preloadChannel, 100ms Preview). Excluded from connectMs.
    opts.push({ level: 'info', text: 'Lobby pre-warm: capturing mics + fetching TURN credentials…' })
    await timeStep('Lobby pre-warm (mic + TURN creds + PC) — excluded from connect time', timeline, async () => {
      await Promise.all([
        session.alice.peer.getUserMedia({ audio: true }),
        session.bob.peer.getUserMedia({ audio: true }),
        session.alice.peer.prepare(),
        session.bob.peer.prepare(),
      ])
    })

    const connectStart = performance.now()
    opts.push({ level: 'info', text: `Joining room "${session.roomId}" — join+publish in one round trip per peer…` })
    await timeStep('Join + publish (1 round trip × 2 peers)', timeline, async () => {
      await Promise.all([
        session.alice.peer.joinAndPublish(session.roomId, { audio: 'audio' }),
        session.bob.peer.joinAndPublish(session.roomId, { audio: 'audio' }),
      ])
    })

    // Both peers share the tab, so each subscriber gates directly on the
    // OTHER publisher's local bytesSent signal (RTP verifiably flowing); the
    // /publish/confirm announcement runs in parallel, off the critical path.
    // Cross-client apps gate on the track_published broadcast instead.
    await timeStep('Track live + subscribe + answer (× 2 peers)', timeline, async () => {
      const aliceLive = session.alice.peer.waitForTracksLive()
      const bobLive = session.bob.peer.waitForTracksLive()
      await Promise.all([
        aliceLive.then(() => session.alice.peer.confirmPublished()),
        bobLive.then(() => session.bob.peer.confirmPublished()),
        bobLive.then(() => session.alice.peer.subscribeTo([{ participant_id: session.bob.participantId, track_names: [ 'audio' ], track_kinds: [ 'audio' ] }])),
        aliceLive.then(() => session.bob.peer.subscribeTo([{ participant_id: session.alice.participantId, track_names: [ 'audio' ], track_kinds: [ 'audio' ] }])),
      ])
    })
    opts.push({ level: 'info', text: 'Both peers subscribed; waiting for ICE + audio packets…' })

    await timeStep('ICE connect (DTLS handshake + STUN)', timeline, async () => {
      await Promise.all([
        session.alice.peer.waitForIceConnected(),
        session.bob.peer.waitForIceConnected(),
      ])
    })

    await timeStep('First audio packet flowing', timeline, async () => {
      await Promise.all([
        waitFor(
          () => session.alice.peer.getInboundStats(),
          (stats) => stats.some((s) => s.kind === 'audio' && s.packetsReceived > 0),
          { timeoutMs: 8_000, intervalMs: 100, label: 'alice receives audio' }
        ),
        waitFor(
          () => session.bob.peer.getInboundStats(),
          (stats) => stats.some((s) => s.kind === 'audio' && s.packetsReceived > 0),
          { timeoutMs: 8_000, intervalMs: 100, label: 'bob receives audio' }
        ),
      ])
    })

    const elapsed = (performance.now() - started) / 1000
    const aliceRx = summarize(await session.alice.peer.getInboundStats(), elapsed)
    const bobRx = summarize(await session.bob.peer.getInboundStats(), elapsed)
    opts.push({ level: 'success', text: `Audio packets flowing — alice rx ${aliceRx[0]?.packets ?? 0}, bob rx ${bobRx[0]?.packets ?? 0}` })

    const totalMs = Math.round(performance.now() - started)
    const connectMs = Math.round(performance.now() - connectStart)
    return {
      result: { ok: true, durationMs: totalMs, timeline, connectMs, stats: { aliceRx, bobRx } },
      session,
    }
  } catch (err) {
    return {
      result: {
        ok: false,
        durationMs: Math.round(performance.now() - started),
        error: err instanceof Error ? err.message : String(err),
        timeline,
        connectMs: null,
        stats: { aliceRx: [], bobRx: [] },
      },
      session,
    }
  }
}

export async function runVideoScenario(opts: { basePath: string; push: ConsolePush }): Promise<{ result: MediaResult; session: MediaSession }> {
  const started = performance.now()
  const timeline: TimingStep[] = []
  const session = createSession(opts, 'video')
  try {
    // Lobby pre-warm — see the audio scenario for details.
    opts.push({ level: 'info', text: 'Lobby pre-warm: capturing camera+mic + fetching TURN credentials…' })
    await timeStep('Lobby pre-warm (camera + mic + TURN creds + PC) — excluded from connect time', timeline, async () => {
      await Promise.all([
        session.alice.peer.getUserMedia({ audio: true, video: true }),
        session.bob.peer.getUserMedia({ audio: true, video: true }),
        session.alice.peer.prepare(),
        session.bob.peer.prepare(),
      ])
    })

    const connectStart = performance.now()
    opts.push({ level: 'info', text: `Joining room "${session.roomId}" — join+publish in one round trip per peer…` })
    await timeStep('Join + publish (1 round trip × 2 peers)', timeline, async () => {
      await Promise.all([
        session.alice.peer.joinAndPublish(session.roomId, { audio: 'audio', video: 'video' }),
        session.bob.peer.joinAndPublish(session.roomId, { audio: 'audio', video: 'video' }),
      ])
    })

    // Subscribe fires per side as soon as the other publisher's RTP is
    // verifiably flowing; confirm runs off-path — see the audio scenario.
    await timeStep('Track live + subscribe + answer (× 2 peers)', timeline, async () => {
      const aliceLive = session.alice.peer.waitForTracksLive()
      const bobLive = session.bob.peer.waitForTracksLive()
      await Promise.all([
        aliceLive.then(() => session.alice.peer.confirmPublished()),
        bobLive.then(() => session.bob.peer.confirmPublished()),
        bobLive.then(() => session.alice.peer.subscribeTo([{ participant_id: session.bob.participantId, track_names: [ 'audio', 'video' ], track_kinds: [ 'audio', 'video' ] }])),
        aliceLive.then(() => session.bob.peer.subscribeTo([{ participant_id: session.alice.participantId, track_names: [ 'audio', 'video' ], track_kinds: [ 'audio', 'video' ] }])),
      ])
    })
    opts.push({ level: 'info', text: 'Both peers subscribed; waiting for ICE + video frames…' })

    await timeStep('ICE connect (DTLS handshake + STUN)', timeline, async () => {
      await Promise.all([
        session.alice.peer.waitForIceConnected(),
        session.bob.peer.waitForIceConnected(),
      ])
    })

    await timeStep('First video frame decoded', timeline, async () => {
      await Promise.all([
        waitFor(
          () => session.alice.peer.getInboundStats(),
          (stats) => stats.some((s) => s.kind === 'video' && (s.framesDecoded ?? 0) > 0),
          { timeoutMs: 12_000, intervalMs: 100, label: 'alice decodes video' }
        ),
        waitFor(
          () => session.bob.peer.getInboundStats(),
          (stats) => stats.some((s) => s.kind === 'video' && (s.framesDecoded ?? 0) > 0),
          { timeoutMs: 12_000, intervalMs: 100, label: 'bob decodes video' }
        ),
      ])
    })

    const elapsed = (performance.now() - started) / 1000
    const aliceRx = summarize(await session.alice.peer.getInboundStats(), elapsed)
    const bobRx = summarize(await session.bob.peer.getInboundStats(), elapsed)
    opts.push({ level: 'success', text: `Video frames decoded — alice ${aliceRx.find((s) => s.kind === 'video')?.frames ?? 0}, bob ${bobRx.find((s) => s.kind === 'video')?.frames ?? 0}` })

    const totalMs = Math.round(performance.now() - started)
    const connectMs = Math.round(performance.now() - connectStart)
    return {
      result: { ok: true, durationMs: totalMs, timeline, connectMs, stats: { aliceRx, bobRx } },
      session,
    }
  } catch (err) {
    return {
      result: {
        ok: false,
        durationMs: Math.round(performance.now() - started),
        error: err instanceof Error ? err.message : String(err),
        timeline,
        connectMs: null,
        stats: { aliceRx: [], bobRx: [] },
      },
      session,
    }
  }
}

export async function runScreenScenario(opts: { basePath: string; push: ConsolePush }): Promise<{ result: MediaResult; session: MediaSession }> {
  const started = performance.now()
  const timeline: TimingStep[] = []
  const session = createSession(opts, 'screen')
  try {
    // Alice picks a screen share; Bob is the viewer. The picker prompt and
    // credential pre-warm run before the connect clock starts.
    opts.push({ level: 'info', text: 'Lobby pre-warm: pick a window to share (browser will prompt)…' })
    await timeStep('Lobby pre-warm (getDisplayMedia + TURN creds + PC) — excluded from connect time', timeline, async () => {
      await Promise.all([
        session.alice.peer.getDisplayMedia(),
        session.alice.peer.prepare(),
        session.bob.peer.prepare(),
      ])
    })

    const connectStart = performance.now()
    opts.push({ level: 'info', text: 'Screen captured. Joining + publishing screen_video; Bob joins as viewer…' })
    await timeStep('Join + publish screen (Alice) ∥ join (Bob)', timeline, async () => {
      await Promise.all([
        session.alice.peer.joinAndPublish(session.roomId, { screen_video: 'screen_video' }),
        session.bob.peer.joinRoom(session.roomId, 'group'),
      ])
    })

    await timeStep('Track live + Bob subscribes', timeline, async () => {
      await session.alice.peer.waitForTracksLive()
      await Promise.all([
        session.alice.peer.confirmPublished(),
        session.bob.peer.subscribeTo([{ participant_id: session.alice.participantId, track_names: [ 'screen_video' ], track_kinds: [ 'video' ] }]),
      ])
    })

    await timeStep('ICE connect (Bob)', timeline, () => session.bob.peer.waitForIceConnected())

    await timeStep('First screen frame decoded', timeline, () =>
      waitFor(
        () => session.bob.peer.getInboundStats(),
        (stats) => stats.some((s) => s.kind === 'video' && (s.framesDecoded ?? 0) > 0),
        { timeoutMs: 15_000, intervalMs: 100, label: 'bob decodes screen frames' }
      ).then(() => undefined)
    )

    const elapsed = (performance.now() - started) / 1000
    const bobRx = summarize(await session.bob.peer.getInboundStats(), elapsed)
    opts.push({ level: 'success', text: `Screen frames flowing — bob decoded ${bobRx[0]?.frames ?? 0} frames at ${bobRx[0]?.resolution ?? '?'}` })

    const totalMs = Math.round(performance.now() - started)
    const connectMs = Math.round(performance.now() - connectStart)
    return {
      result: { ok: true, durationMs: totalMs, timeline, connectMs, stats: { aliceRx: [], bobRx } },
      session,
    }
  } catch (err) {
    return {
      result: {
        ok: false,
        durationMs: Math.round(performance.now() - started),
        error: err instanceof Error ? err.message : String(err),
        timeline,
        connectMs: null,
        stats: { aliceRx: [], bobRx: [] },
      },
      session,
    }
  }
}
